import 'dayjs/locale/en-au'; // Import Australian locale

import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Button, Divider, Flex, Form, Menu, MenuProps, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';
import { FC, useCallback, useEffect, useState } from 'react';

import { determineDateRange } from '../../util';
import { SHORT_DATE_FORMAT } from '../../util/constants';
import { FloatFormItem } from '../FloatFormItem';
import { FloatDateInput } from '../FloatPhoneInput';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

// Configure Australian locale with Monday as first day of week
dayjs.updateLocale('en-au', {
  weekStart: 1, // 1 = Monday
});
dayjs.locale('en-au');

// Quick filter options for date ranges
const quickFilters = [
  { label: 'Today', key: 'today' },
  { label: 'This Week', key: 'thisWeek' },
  { label: 'Last Week', key: 'lastWeek' },
  { label: 'This Month', key: 'thisMonth' },
  { label: 'Last 7 days', key: 'last7Day' },
  { label: 'Last 14 days', key: 'last14Day' },
  { label: 'Last 30 days', key: 'last30Day' },
  { label: 'Last 45 days', key: 'last45Day' },
  { label: 'Last 60 days', key: 'last60Day' },
  { label: 'Custom', key: 'custom' },
];

export type DatePickerType = Dayjs | undefined | null;

interface RangePickerProps {
  label?: string;
  values: [DatePickerType, DatePickerType];
  onChange: (values: [DatePickerType, DatePickerType], type?: string) => void;
  widthClassname?: string;
}

/**
 * A customizable date range picker component with quick filters
 * Features:
 * - Dual calendar view
 * - Quick filter presets
 * - Custom date range selection
 * - Form input validation
 * - Australian locale support (Monday-first week)
 */
export const RangePicker: FC<RangePickerProps> = ({
  label = '',
  values,
  onChange,
  widthClassname = 'w-fit',
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [nextMonth, setNextMonth] = useState<Dayjs>(
    currentDate.add(1, 'month')
  );
  const [startDate, setStartDate] = useState<DatePickerType>(null);
  const [endDate, setEndDate] = useState<DatePickerType>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [form] = Form.useForm();

  // Form field watchers
  const startDateForm = Form.useWatch('startDate', form);
  const endDateForm = Form.useWatch('endDate', form);

  // Initialize modal with current values when opened
  useEffect(() => {
    if (values && values[0] && values[1] && openModal) {
      setStartDate(values[0]);
      setEndDate(values[1]);
      setCurrentDate(values[0]);
      setNextMonth(values[0].add(1, 'month'));
      const selectFilter = determineDateRange(values[0], values[1]);
      setSelectedFilter(selectFilter ?? '');
      form.setFieldsValue({
        startDate: values[0] ? values[0].format(SHORT_DATE_FORMAT) : '',
        endDate: values[1] ? values[1].format(SHORT_DATE_FORMAT) : '',
      });
    }
  }, [form, openModal, values]);

  // Update form fields when dates change
  useEffect(() => {
    form.setFieldsValue({
      startDate: startDate ? startDate.format(SHORT_DATE_FORMAT) : '',
      endDate: endDate ? endDate.format(SHORT_DATE_FORMAT) : '',
    });
  }, [startDate, endDate, form]);

  // Handle form field changes
  useEffect(() => {
    if (
      startDateForm &&
      endDateForm &&
      startDateForm.length === 10 &&
      endDateForm.length === 10
    ) {
      const start = dayjs(startDateForm, SHORT_DATE_FORMAT);
      const end = dayjs(endDateForm, SHORT_DATE_FORMAT);
      if (isSameDay(start, end)) {
        setStartDate(start);
        setEndDate(start);
        const selectFilter = determineDateRange(start, start);
        setSelectedFilter(selectFilter ?? '');
      } else if (end.isAfter(start)) {
        setStartDate(start);
        setEndDate(end);
        const selectFilter = determineDateRange(start, end);
        setSelectedFilter(selectFilter ?? '');
      } else {
        // Invalid range - end before start
        setStartDate(end);
        setEndDate(null);
        form.setFieldValue('endDate', null);
      }
    }
  }, [endDateForm, form, startDateForm]);

  // Close modal and reset state
  const handleCancel = useCallback(() => {
    setOpenModal(false);
    setStartDate(null);
    setEndDate(null);
    setSelectedFilter('');
  }, []);

  // Apply selected date range
  const handleApply = useCallback(() => {
    const values = form.getFieldsValue();
    if (!values.startDate) {
      form.setFields([
        {
          name: 'startDate',
          errors: [''],
        },
      ]);
      return;
    }
    if (!values.endDate) {
      form.setFields([
        {
          name: 'endDate',
          errors: [''],
        },
      ]);
      return;
    }

    // Call parent onChange with selected range
    onChange?.(
      [startDate, endDate],
      selectedFilter === 'custom' ? undefined : selectedFilter
    );
    handleCancel();
  }, [form, onChange, startDate, endDate, selectedFilter, handleCancel]);

  // Generate days for calendar view
  const generateCalendarDays = (date: Dayjs): Dayjs[] => {
    const start = date.startOf('month');
    const end = date.endOf('month');
    const days: Dayjs[] = [];

    let currentDay = start;
    while (currentDay.isBefore(end) || currentDay.isSame(end, 'day')) {
      days.push(currentDay);
      currentDay = currentDay.add(1, 'day');
    }

    return days;
  };

  // Handle quick filter selection
  const handleQuickFilter: MenuProps['onClick'] = (e) => {
    setSelectedFilter(e.key);

    const today = dayjs();
    let start: Dayjs | undefined;
    let end: Dayjs | undefined;

    // Set date range based on selected filter
    switch (e.key) {
      case 'today':
        start = today.startOf('day');
        end = today.endOf('day');
        break;
      case 'thisWeek':
        start = today.startOf('week');
        end = today.endOf('week');
        break;
      case 'lastWeek':
        start = today.subtract(1, 'week').startOf('week');
        end = today.subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        start = today.startOf('month');
        end = today.endOf('month');
        break;
      case 'last7Day':
        start = today.subtract(7, 'days');
        end = today;
        break;
      case 'last14Day':
        start = today.subtract(14, 'days');
        end = today;
        break;
      case 'last30Day':
        start = today.subtract(30, 'days');
        end = today;
        break;
      case 'last45Day':
        start = today.subtract(45, 'days');
        end = today;
        break;
      case 'last60Day':
        start = today.subtract(60, 'days');
        end = today;
        break;
      case 'custom':
        start = undefined;
        end = undefined;
        break;
      default:
        break;
    }

    setStartDate(start || null);
    setEndDate(end || null);
    setCurrentDate(start || today);
    setNextMonth(start ? start.add(1, 'month') : today.add(1, 'month'));
  };

  // Handle date selection in calendar
  const handleDateClick = (date: Dayjs) => {
    if (!startDate || (startDate && endDate)) {
      // First selection or reset selection
      setStartDate(date);
      setEndDate(null);
    } else {
      if (isSameDay(startDate, date)) {
        // Same date clicked
        setStartDate(date);
        setEndDate(date);
      } else if (date.isBefore(startDate)) {
        // Earlier date clicked
        setStartDate(date);
        setEndDate(null);
      } else {
        // Later date clicked (completing range)
        setEndDate(date);
      }
    }
  };

  // Check if date is within selected range
  const isInRange = (date: Dayjs): boolean => {
    return (
      !!startDate &&
      !!endDate &&
      date.isSameOrAfter(startDate, 'day') &&
      date.isSameOrBefore(endDate, 'day')
    );
  };

  // Check if two dates are the same day
  const isSameDay = (date1: DatePickerType, date2: DatePickerType): boolean => {
    return (
      !!date1 &&
      !!date2 &&
      date1.format(SHORT_DATE_FORMAT) === date2.format(SHORT_DATE_FORMAT)
    );
  };

  const isToday = (date: Dayjs): boolean => {
    return date.isSame(dayjs(), 'day');
  };

  /**
   * Generates a calendar component for month based on the given date.
   * @param date The date to generate the calendar for.
   * @returns A JSX element representing the calendar.
   */
  const renderCalendar = (date: Dayjs) => {
    const days = generateCalendarDays(date);
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <Flex vertical className="w-1/2 bg-white rounded-lg p-3">
        <Flex vertical align="center" justify="between" className="mb-4">
          <h2 className="text-base font-semibold">
            {date.format('MMMM YYYY')}
          </h2>
        </Flex>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="w-10 h-8 text-center text-sm text-gray-600"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({
            length: days[0].day() === 0 ? 6 : days[0].day() - 1,
          }).map((_, idx) => (
            <div key={`empty-start-${idx}`} className="p-2" />
          ))}
          {days.map((day, idx) => (
            <button
              key={idx}
              onClick={() => handleDateClick(day)}
              className={`w-10 h-10 text-sm rounded-lg hover:bg-gray-100
                ${
                  isSameDay(day, startDate) ? 'bg-orange-600 text-white' : ''
                } ${isSameDay(day, endDate) ? 'bg-orange-600 text-white' : ''} ${
                  isInRange(day) ? 'bg-orange-100 hover:bg-orange-200' : ''
                }
                ${isToday(day) && !isSameDay(day, startDate) && !isSameDay(day, endDate) ? 'border border-orange-600 text-orange-600' : ''}
              `}
            >
              {day.format('D')}
            </button>
          ))}
        </div>
      </Flex>
    );
  };

  return (
    <Flex vertical className="w-full">
      {/* Trigger button */}
      <Flex
        gap={8}
        align="center"
        justify="space-between"
        onClick={() => setOpenModal(true)}
        className={`border cursor-pointer border-gray-300 hover:border-orange-600 rounded-lg px-3 ${widthClassname} h-10`}
      >
        <div className="w-fit whitespace-nowrap">
          <span className="font-semibold whitespace-nowrap">{label}&nbsp;</span>
          {values[0]
            ? values[0].format(SHORT_DATE_FORMAT)
            : 'Start date'} -{' '}
          {values[1] ? values[1].format(SHORT_DATE_FORMAT) : 'End date'}
        </div>
        <CalendarOutlined className="w-4 h-4" />
      </Flex>
      {/* Date picker modal */}
      <Modal
        closeIcon={false}
        open={openModal}
        width={780}
        destroyOnClose
        centered
        footer={null}
        styles={{ content: { padding: 0 } }}
      >
        <Flex className="bg-white rounded-lg w-full p-2 justify-between">
          {/* Quick filters sidebar */}
          <Flex vertical className="flex-1">
            <Menu
              onClick={handleQuickFilter}
              selectedKeys={[selectedFilter]}
              mode="vertical"
              items={quickFilters}
            />
          </Flex>
          <Flex vertical className="w-[630px] ">
            <Flex className="w-full relative">
              {/* Calendar and form section */}
              <Flex
                justify="space-between"
                align="center"
                className="absolute w-full mb-4 p-3 py-2"
              >
                <Button
                  shape="circle"
                  onClick={() => {
                    setCurrentDate(currentDate.subtract(1, 'month'));
                    setNextMonth(nextMonth.subtract(1, 'month'));
                  }}
                  icon={<ArrowLeftOutlined />}
                  className="hover:bg-gray-100"
                />
                <Button
                  shape="circle"
                  onClick={() => {
                    setCurrentDate(currentDate.add(1, 'month'));
                    setNextMonth(nextMonth.add(1, 'month'));
                  }}
                  icon={<ArrowRightOutlined />}
                  className="hover:bg-gray-100"
                />
              </Flex>
              {/* Dual calendar view */}
              {renderCalendar(currentDate)}
              <Divider type="vertical" className="h-full" />
              {renderCalendar(nextMonth)}
            </Flex>
            {/* Date inputs and action buttons */}
            <Flex
              className="border-t w-full border-gray-200 p-6"
              justify="space-between"
            >
              <Form
                form={form}
                initialValues={{}}
                // onFinish={onFinish}
                clearOnDestroy
                layout="vertical"
                autoComplete="off"
              >
                <FloatFormItem
                  label="Start date"
                  name="startDate"
                  className="mb-0 w-28 inline-flex"
                >
                  <FloatDateInput />
                </FloatFormItem>
                <Flex className="inline-flex h-10 px-2" align="center">
                  -
                </Flex>
                <FloatFormItem
                  label="End date"
                  name="endDate"
                  className="mb-0 w-28 inline-flex"
                >
                  <FloatDateInput />
                </FloatFormItem>
              </Form>
              <Flex gap={4}>
                <Button
                  size="large"
                  className="font-semibold"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  variant="solid"
                  color="primary"
                  className="font-semibold"
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Modal>
    </Flex>
  );
};
